import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { Tab, Tabs, Tooltip, OverlayTrigger } from 'react-bootstrap';
import moment from 'moment';

import { Link } from 'react-router-dom';
import Fa from 'react-fontawesome';
import ProgressBar from '../../../../components/progress-bar';
import SearchForm from '../../../../components/search-form';
import PeriodField from '../../components/period-field/period-field';
import ForecastNav from '../../components/nav';
// Import Actions
import { fetchOpportunities, reset, fetchForcastPeriods } from '../../ForecastActions';
import NumberDisplay from '../../../../components/number-display';

// Import Selectors
import { getForcasts, getLoading, getLoaded, getErrors, getHasErrors,
  getCurrentOpportunities, getLostOpportunities,
  getWonOpportunities, getSlippedOpportunities, getNewOpportunities,
  getPeriodRange, getBeginningStats, getCurrentStats, getLostStats, getForecastId,
  getSlippedStats, getWonStats, getNewStats, getForcastPeriods, getIsFutureForecast } from '../../ForecastReducer';
import ForecastTable from '../../components/forecast-table/forecast-table';
import OpportunityTable from '../../components/opportunity-table/opportunity-table';

const TABLE_NAME = 'forecast-current-table';

class CurrentForecastPage extends Component {
  constructor() {
    super();
    this.state = {
      loadingPeriods: true,
      period: [moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')],
      currentSearchParams: null,
    };

    this.handleFormChange = this.handleFormChange.bind(this);
    this.handleReset = this.handleReset.bind(this);
  }

  componentDidMount() {
    this.loadData();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.isWeighted !== nextProps.isWeighted) {
      this.props.dispatch(fetchOpportunities(this.state.currentSearchParams, nextProps.isWeighted));
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleFormChange(currentSearchParams) {
    if (this.mounted) {
      this.setState({ currentSearchParams }, () => {
        this.props.dispatch(fetchOpportunities(currentSearchParams, this.props.isWeighted))
          .then(
            (results) => {
              const { forecasts, total } = results;
              const returnResults = forecasts.length;
              if (total && returnResults < total) {
                const el = (
                  <div>
                    Your search return more than <NumberDisplay value={total} precision={0} /> results.<br />
                    For optimal performance, only the latest <NumberDisplay value={returnResults} precision={0} /> will be displayed.
                  </div>
                );
                this.context.addNotification(el, { type: 'warning', autoClose: 30 * 1000 });
              }
            }
          );
      });
    }
  }

  handleReset() {
    this.props.dispatch(reset());
  }

  loadData() {
    this.props.dispatch(fetchForcastPeriods())
      .then(() => {
        this.setState({ loadingPeriods: false });
      });
    this.props.dispatch(reset()).then(() => {
      this.mounted = true;
    });
  }

  renderEmptyMessage() {
    return (
      <div className="alert alert-danger">
        <p><strong><Fa name="exclamation-triangle" /> No records found matching your criteria.</strong></p>
        <p>Make sure your <Link to="/setup">mappings</Link> are setup correctly or broaden your search criteria to see more results.</p>
      </div>
    );
  }

  renderError() {
    const { errors, hasErrors } = this.props;
    if (!hasErrors) {
      return null;
    }
    let message = errors.messge;
    if (errors.data) {
      message = errors.data.message || errors.data;
    }
    return (
      <div className="alert alert-danger">
        <Fa name="exclamation-triangle" /> {message}
      </div>
    );
  }

  renderTopTable() {
    const {
      isFutureForecast, isWeighted, periodRange,
    } = this.props;
    let forecastTableTitle = isWeighted ? 'Weighted Forecast' : 'Current Forecast';
    if (isFutureForecast) {
      forecastTableTitle = isWeighted ? `Weighted Forecast for ${periodRange}` : `Forecast for ${periodRange}`;
    }
    return (
      <ForecastTable
        isFuture={isFutureForecast}
        isWeighted={isWeighted}
        title={forecastTableTitle}
        location={this.props.location}
      />
    );
  }

  render() {
    const {
      loading, loaded, hasErrors, forecasts, currentOpportunities, periodRange,
      wonOpportunities, lostOpportunities, slippedOpportunities, newOpportunities, forecastId,
      beginningStats, currentStats, lostStats, slippedStats, wonStats, newStats, periods, isFutureForecast,
      isWeighted,
    } = this.props;
    const { loadingPeriods } = this.state;
    const total = forecasts.length;

    const beginningTooltip = (<Tooltip id="beginning-tooltip">
      Opportunities originally forecast to close for the {periodRange} forecast period.
    </Tooltip>);
    const currentTooltip = (<Tooltip id="current-tooltip">
      Opportunities currently forecast to close for the {periodRange} forecast period.
    </Tooltip>);
    const wonTooltip = (<Tooltip id="won-tooltip">
      Opportunities won in the {periodRange} forecast period.
    </Tooltip>);
    const slippedTooltip = (<Tooltip id="slipped-tooltip">
      Opportunities originally forecast to close in the {periodRange} forecast period, but now have close dates that fall after the end of this forecast period.
    </Tooltip>);
    const lostTooltip = (<Tooltip id="lost-tooltip">
      Opportunities lost in the {periodRange} forecast period.
    </Tooltip>);
    const newTooltip = (<Tooltip id="new-tooltip">
      New opportunities added to the {periodRange} forecast period after the period began.
    </Tooltip>);

    return (
      <div>
        <Helmet title="Forecast" />
        <div className="container">
          <div className="clearfix">
            <div className="pull-left">
              <ForecastNav />
            </div>
            <div className="pull-right">
              {loadingPeriods && <Fa name="spinner" spin />}
              {!loadingPeriods && <SearchForm
                onSearchChange={this.handleFormChange}
                onReset={this.handleReset}
                pullRight
                disabledRootLogic
                defaultCriteria={{
                  type: 'groupAND',
                  values: [],
                }}
                type="forecast/current"
                queryParams={{ forecast: true, period: this.state.period }}
                disabledFilters={['stage']}
              >
                <PeriodField
                  periods={periods}
                  path={[]}
                  level={1}
                  showDelete={false}
                  showInclude={false}
                  searchCriteria={{
                    _id: 'forecast-period',
                    type: 'include',
                    filter: {
                      _id: 'forecast-period-filter',
                      label: 'Forecast Period',
                    },
                  }}
                  values={this.state.period}
                  onChange={(values) => { this.setState({ period: values }); }}
                />
              </SearchForm>}
            </div>
          </div>
          <p />
          {!loaded && <div className={`${(loading ? '' : 'invisible')}`}><ProgressBar /></div>}
          {loaded && total === 0 && this.renderEmptyMessage()}
        </div>
        {loaded && total > 0 &&
          <div>
            {this.renderTopTable()}

            <div className="container">
              {isFutureForecast &&
                <div>
                  <OpportunityTable
                    showSalesStage
                    stats={currentStats}
                    context={{ footerSumField: 'opportunity_amount' }}
                    opportunities={currentOpportunities}
                    forecastId={forecastId}
                    showTypeChart
                    showSizeChart
                    showCycleChart
                    tableName={TABLE_NAME}
                  />
                </div>
              }

              {!isFutureForecast &&
                <Tabs mountOnEnter unmountOnExit defaultActiveKey="current" animation={false} id="noanim-tab-example">
                  <Tab
                    eventKey="current"
                    title={
                      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={currentTooltip}>
                        <span>
                          Current
                          {currentOpportunities.length ? <span>&nbsp;<span className="badge">
                            <NumberDisplay value={currentOpportunities.length} precision={0} />
                          </span>
                          </span> : null}
                        </span>
                      </OverlayTrigger>
                    }
                  >
                    <OpportunityTable
                      showSalesStage
                      stats={currentStats}
                      context={{ footerSumField: 'opportunity_amount' }}
                      opportunities={currentOpportunities}
                      forecastId={forecastId}
                      showTypeChart
                      showSizeChart
                      showCycleChart
                      tableName={TABLE_NAME}
                      isWeighted={isWeighted}
                    />
                  </Tab>

                  <Tab
                    eventKey="beginning"
                    title={
                      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={beginningTooltip}>
                        <span>
                          Beginning
                          {forecasts.length ? <span>&nbsp;<span className="badge">
                            <NumberDisplay value={forecasts.length} precision={0} />
                          </span>
                          </span> : null}
                        </span>
                      </OverlayTrigger>
                    }
                  >
                    <OpportunityTable
                      showSalesStage
                      isBeginning
                      stats={beginningStats}
                      opportunities={forecasts}
                      forecastId={forecastId}
                      showTypeChart
                      showSizeChart
                      showCycleChart
                      tableName={TABLE_NAME}
                      isWeighted={isWeighted}
                    />
                  </Tab>

                  <Tab
                    eventKey="won"
                    title={
                      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={wonTooltip}>
                        <span>
                          Won
                          {wonOpportunities.length ? <span>&nbsp;<span className="badge">
                            <NumberDisplay value={wonOpportunities.length} precision={0} />
                          </span>
                          </span> : null}
                        </span>
                      </OverlayTrigger>
                    }
                  >
                    <OpportunityTable
                      showStage
                      stats={wonStats}
                      opportunities={wonOpportunities}
                      forecastId={forecastId}
                      showTypeChart
                      showSizeChart
                      showCycleChart
                      tableName={TABLE_NAME}
                      isWeighted={isWeighted}
                    />
                  </Tab>

                  <Tab
                    eventKey="new"
                    title={
                      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={newTooltip}>
                        <span>
                          New
                          {newOpportunities.length ? <span>&nbsp;<span className="badge">
                            <NumberDisplay value={newOpportunities.length} precision={0} />
                          </span>
                          </span> : null}
                        </span>
                      </OverlayTrigger>
                    }
                  >
                    <OpportunityTable
                      showStage
                      stats={newStats}
                      opportunities={newOpportunities}
                      forecastId={forecastId}
                      showTypeChart
                      showSizeChart
                      showCycleChart
                      tableName={TABLE_NAME}
                      isWeighted={isWeighted}
                    />
                  </Tab>

                  <Tab
                    eventKey="slipped"
                    title={
                      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={slippedTooltip}>
                        <span>
                          Slipped
                          {slippedOpportunities.length ? <span>&nbsp;<span className="badge">
                            <NumberDisplay value={slippedOpportunities.length} precision={0} />
                          </span>
                          </span> : null}
                        </span>
                      </OverlayTrigger>
                    }
                  >
                    <OpportunityTable
                      showSalesStage
                      stats={slippedStats}
                      opportunities={slippedOpportunities}
                      forecastId={forecastId}
                      showTypeChart
                      showSizeChart
                      showCycleChart
                      tableName={TABLE_NAME}
                      isWeighted={isWeighted}
                    />
                  </Tab>

                  <Tab
                    eventKey="lost"
                    title={
                      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={lostTooltip}>
                        <span>
                          Lost
                          {lostOpportunities.length ? <span>&nbsp;<span className="badge">
                            <NumberDisplay value={lostOpportunities.length} precision={0} />
                          </span>
                          </span> : null}
                        </span>
                      </OverlayTrigger>
                    }
                  >
                    <OpportunityTable
                      showStage
                      stats={lostStats}
                      opportunities={lostOpportunities}
                      forecastId={forecastId}
                      showTypeChart
                      showSizeChart
                      showCycleChart
                      tableName={TABLE_NAME}
                      isWeighted={isWeighted}
                    />
                  </Tab>
                </Tabs>
              }
            </div>
          </div>
        }
        <div className="container">
          {hasErrors && this.renderError()}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state, ownProps) {
  const { location: { pathname } } = ownProps;
  const isWeighted = pathname === '/forecast/current/weighted';
  return {
    forecasts: getForcasts(state),
    loading: getLoading(state),
    loaded: getLoaded(state),
    errors: getErrors(state),
    hasErrors: getHasErrors(state),
    currentOpportunities: getCurrentOpportunities(state),
    lostOpportunities: getLostOpportunities(state),
    slippedOpportunities: getSlippedOpportunities(state),
    wonOpportunities: getWonOpportunities(state),
    newOpportunities: getNewOpportunities(state),
    periodRange: getPeriodRange(state),
    periods: getForcastPeriods(state),
    beginningStats: getBeginningStats(state),
    currentStats: getCurrentStats(state),
    lostStats: getLostStats(state),
    slippedStats: getSlippedStats(state),
    wonStats: getWonStats(state),
    newStats: getNewStats(state),
    isFutureForecast: getIsFutureForecast(state),
    forecastId: getForecastId(state),
    isWeighted,
  };
}

CurrentForecastPage.propTypes = {
  forecasts: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  errors: PropTypes.shape({}),
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.shape({}).isRequired,
  hasErrors: PropTypes.bool.isRequired,
  currentOpportunities: PropTypes.array.isRequired,
  lostOpportunities: PropTypes.array.isRequired,
  wonOpportunities: PropTypes.array.isRequired,
  slippedOpportunities: PropTypes.array.isRequired,
  newOpportunities: PropTypes.array.isRequired,
  periodRange: PropTypes.string.isRequired,
  beginningStats: PropTypes.shape({}).isRequired,
  currentStats: PropTypes.shape({}).isRequired,
  lostStats: PropTypes.shape({}).isRequired,
  slippedStats: PropTypes.shape({}).isRequired,
  wonStats: PropTypes.shape({}).isRequired,
  newStats: PropTypes.shape({}).isRequired,
  periods: PropTypes.array.isRequired,
  isFutureForecast: PropTypes.bool.isRequired,
  forecastId: PropTypes.string,
  isWeighted: PropTypes.bool.isRequired,
};

CurrentForecastPage.contextTypes = {
  router: PropTypes.object,
  addNotification: PropTypes.func.isRequired,
};
export default connect(mapStateToProps)(CurrentForecastPage);
